import classNames from 'classnames'
import {
  ArrowRightSmIcon,
  BellIcon,
  ColumnSkeleton,
  DownloadIcon,
  LoadingSpinner,
  LogoutIcon,
  QuestionIcon,
  RenderIf,
} from 'components/Basic'
import {colorCodes} from 'definitions/constant'
import {selectUser} from 'features/User'
import {RoleNames, SliceStatus, ValidUserTypes} from 'interfaces'
import {FC, ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'

interface SwitchAccountProps {
  selectedTenant: string
  onAccountSwitch: (e: React.MouseEvent<HTMLElement>, value: string) => void
  isTopBarToBeBlueColor: boolean
}

const SwitchAccount: FC<SwitchAccountProps> = ({
  selectedTenant,
  onAccountSwitch,
  isTopBarToBeBlueColor,
}) => {
  const {primaryDomain, multiAccount, tokenStatus} = useSelector(selectUser)

  return (
    <>
      <div
        className="text-sm pt-4 mb-1 px-6 font-normal flex cursor-pointer w-full justify-start items-center"
        onClick={e => e.stopPropagation()}
      >
        <p id="name" className="karla cursor-pointer ">
          Switch Account
        </p>
      </div>
      <section className="pb-1">
        {multiAccount?.details?.listOfDomains?.length ? (
          <div
            className="overflow-y-auto overflow-x-hidden max-h-32"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-6 py-1 flex justify-start items-center">
              <p
                className="karla text-xs font-normal text-white"
                data-cy="current-user-name"
              >
                <span className="text-gray-1100">{primaryDomain}</span>
                {'  '}
                <span className="text-green-100">(current)</span>
              </p>
              <ArrowRightSmIcon
                classes="ml-auto"
                strokeColor={colorCodes.gray1100}
              />
            </div>
            <div className="mx-6 border-b-1 border-gray-1200"></div>
            {multiAccount?.details?.listOfDomains
              ?.filter(item => item !== primaryDomain)
              .map(tenant => (
                <>
                  <div
                    className={classNames(
                      'px-6 py-1 flex cursor-pointer justify-between items-center',
                      {
                        'hover:bg-gray-800': !isTopBarToBeBlueColor,
                        'hover:bg-dope-teal-300': isTopBarToBeBlueColor,
                      },
                    )}
                    onClick={e => onAccountSwitch(e, tenant)}
                  >
                    <p
                      className="karla pl-px text-xs text-white underline decoration-0"
                      data-cy="switch-account-tenant"
                    >
                      {tenant}
                    </p>
                    {tokenStatus === SliceStatus.pending &&
                    selectedTenant === tenant ? (
                      <span
                        className="ml-auto pb-2"
                        onClick={e => e.stopPropagation()}
                      >
                        <LoadingSpinner
                          type="TailSpin"
                          color="#FFFFFF"
                          height={16}
                          width={16}
                        />
                      </span>
                    ) : (
                      <ArrowRightSmIcon classes="ml-auto cursor-pointer" />
                    )}
                  </div>
                  <div className="mx-6 border-t-1 border-gray-1200"></div>
                </>
              ))}
          </div>
        ) : (
          <>
            <div className="mx-6 pt-2 pb-1 flex cursor-pointer justify-start items-center border-b-1 border-gray-1200">
              <ColumnSkeleton width={200} classes="mb-2" />
            </div>
            <div className="mx-6 pt-2 pb-1 flex cursor-pointer justify-start items-center border-b-1 border-gray-1200">
              <ColumnSkeleton width={200} classes="mb-2" />
            </div>
          </>
        )}
      </section>
    </>
  )
}

// Phones move the header's bell, download and help icons into this menu.
export interface MobileAccountShortcuts {
  canReadNotifications: boolean
  hasPendingNotification: boolean
  onNotifications: () => void
  onDownload: () => void
  onHelp: () => void
}

interface MenuRowProps {
  icon: ReactNode
  label: string
  dataCy: string
  isTopBarToBeBlueColor: boolean
  onClick: (e: React.MouseEvent<HTMLElement>) => void
}

const MenuRow: FC<MenuRowProps> = ({
  icon,
  label,
  dataCy,
  isTopBarToBeBlueColor,
  onClick,
}) => (
  <button
    type="button"
    data-cy={dataCy}
    className={classNames(
      'mt-1 pt-1 px-6 w-full min-h-11 flex flex-col justify-center text-left cursor-pointer',
      {
        'hover:bg-gray-800 active:bg-gray-800': !isTopBarToBeBlueColor,
        'hover:bg-dope-teal-300 active:bg-dope-teal-300': isTopBarToBeBlueColor,
      },
    )}
    onClick={onClick}
  >
    <span className="w-full flex justify-start items-center pb-1">
      {icon}
      <span className="pl-1 karla text-xs text-white underline decoration-0">
        {label}
      </span>
      <ArrowRightSmIcon classes="ml-auto" />
    </span>
    <span className="w-full border-b-1 border-gray-1200" />
  </button>
)

interface LogoutContainerProps {
  mobileShortcuts?: MobileAccountShortcuts
  name: string
  pathname: string
  emailId: string
  selectedTenant: string
  onLogout: (e: React.MouseEvent<HTMLElement>) => void
  onAccountSwitch: (e: React.MouseEvent<HTMLElement>, value: string) => void
  isTopBarToBeBlueColor: boolean
}

export const LogoutContainer: FC<LogoutContainerProps> = ({
  name,
  pathname,
  emailId,
  selectedTenant,
  onLogout,
  onAccountSwitch,
  isTopBarToBeBlueColor,
  mobileShortcuts,
}) => {
  const {role, multiAccount} = useSelector(selectUser)
  const {t} = useTranslation('common')

  return (
    <div
      id="logout"
      className={classNames(
        'py-4 border-l-0.5 border-b-0.5 border-r-0.5 border-gray-400 w-auto min-w-96 z-50 absolute right-6',
        pathname !== '/policies' && 'col-span-4 col-start-11',
        {
          'bg-dope-black': !isTopBarToBeBlueColor,
          'bg-dope-teal-100': isTopBarToBeBlueColor,
        },
      )}
    >
      <p id="name" className="text-sm karla px-6">
        {name}
        <span id="email" className="text-gray-1100">
          &nbsp;({emailId})
        </span>
        <RenderIf
          isTrue={[ValidUserTypes.User, ValidUserTypes.Analyst].includes(role)}
        >
          <>
            &nbsp;&nbsp;
            <span className="text-gray-1100 px-2 border-0.5 rounded-full border-gray-1100">
              {
                {
                  [ValidUserTypes.User]: RoleNames['Read-Only'],
                  [ValidUserTypes.Analyst]: RoleNames.Analyst,
                }[role]
              }
            </span>
          </>
        </RenderIf>
      </p>
      <RenderIf isTrue={Boolean(mobileShortcuts)}>
        <>
          <RenderIf isTrue={Boolean(mobileShortcuts?.canReadNotifications)}>
            <MenuRow
              dataCy="mobile-account-notifications"
              label={t('NOTIFICATIONS')}
              isTopBarToBeBlueColor={isTopBarToBeBlueColor}
              onClick={e => {
                e.stopPropagation()
                mobileShortcuts?.onNotifications()
              }}
              icon={
                <span className="relative flex">
                  <BellIcon strokeColor="text-gray-1000" />
                  <RenderIf
                    isTrue={Boolean(mobileShortcuts?.hasPendingNotification)}
                  >
                    <span
                      data-cy="mobile-account-notification-indicator"
                      className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-dope-purple-50"
                    />
                  </RenderIf>
                </span>
              }
            />
          </RenderIf>
          <MenuRow
            dataCy="mobile-account-download"
            label={t('DOWNLOAD_ENDPOINT_INSTALLER')}
            isTopBarToBeBlueColor={isTopBarToBeBlueColor}
            onClick={e => {
              e.stopPropagation()
              mobileShortcuts?.onDownload()
            }}
            icon={
              <DownloadIcon
                strokeColor="text-gray-1000"
                strokeWidth={1}
                width={24}
                height={24}
              />
            }
          />
          <MenuRow
            dataCy="mobile-account-help"
            label={t('HELP')}
            isTopBarToBeBlueColor={isTopBarToBeBlueColor}
            onClick={e => {
              e.stopPropagation()
              mobileShortcuts?.onHelp()
            }}
            icon={
              <QuestionIcon
                strokeColor="text-gray-1000"
                width={24}
                height={24}
              />
            }
          />
        </>
      </RenderIf>
      <div
        data-cy="logout-action"
        className={classNames('mt-1 pt-1 px-6 cursor-pointer', {
          'hover:bg-gray-800': !isTopBarToBeBlueColor,
          'hover:bg-dope-teal-300': isTopBarToBeBlueColor,
        })}
        onClick={onLogout}
      >
        <div className="flex justify-start items-center pb-1">
          <LogoutIcon />
          <p className="pl-1 karla text-xs text-white underline decoration-0">
            Log Out
          </p>
          <ArrowRightSmIcon classes="ml-auto cursor-pointer" />
        </div>
        <div className="border-b-1 border-gray-1200 px-12"></div>
      </div>
      <RenderIf isTrue={multiAccount?.details?.listOfDomains?.length > 1}>
        <SwitchAccount
          selectedTenant={selectedTenant}
          onAccountSwitch={onAccountSwitch}
          isTopBarToBeBlueColor={isTopBarToBeBlueColor}
        />
      </RenderIf>
    </div>
  )
}
